/**
 * Auth Provider
 * Manages authentication state and provides auth context
 */

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    getCurrentUser,
    logout as logoutAction,
    selectUser,
    selectIsAuthenticated,
    selectAuthLoading,
    selectAuthInitialized,
    selectAuthError,
    clearError,
    skipInitialization,
} from '@/store/slices/authSlice';
import { TokenManager } from '@/shared/api';
import type { User } from '@/types/entities';

// ===========================================
// Types
// ===========================================

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isInitialized: boolean;
    error: string | null;
    logout: () => Promise<void>;
    clearAuthError: () => void;
    isAdmin: boolean;
}

// ===========================================
// Context
// ===========================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ===========================================
// Provider
// ===========================================

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const isLoading = useAppSelector(selectAuthLoading);
    const isInitialized = useAppSelector(selectAuthInitialized);
    const error = useAppSelector(selectAuthError);

    // Check for existing token and fetch user on mount
    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const initializeAuth = () => {
            const token = TokenManager.getToken();
            if (token && !isInitialized) {
                // Token exists, try to get current user
                dispatch(getCurrentUser());

                // Fallback timeout - if API doesn't respond in 10 seconds, skip initialization
                timeoutId = setTimeout(() => {
                    if (!isInitialized) {
                        console.warn('Auth initialization timeout - clearing tokens');
                        TokenManager.clearTokens();
                        dispatch(skipInitialization());
                    }
                }, 10000);
            } else if (!token && !isInitialized) {
                // No token - mark as initialized immediately
                dispatch(skipInitialization());
            }
        };

        initializeAuth();

        // Cleanup timeout on unmount or when isInitialized changes
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [dispatch, isInitialized]);

    // Logout handler
    const logout = async () => {
        await dispatch(logoutAction());
    };

    // Clear error handler
    const clearAuthError = () => {
        dispatch(clearError());
    };

    // Check if user is admin
    const isAdmin = user?.role === 'admin';

    const value: AuthContextType = {
        user,
        isAuthenticated,
        isLoading,
        isInitialized,
        error,
        logout,
        clearAuthError,
        isAdmin,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ===========================================
// Hook
// ===========================================

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

/**
 * Returns the auth context when one is mounted, or `null` otherwise.
 *
 * For components that render in both authed app contexts and standalone
 * contexts (e.g. PropertyCard which is exercised by Vitest suites that
 * mount it in isolation without an AuthProvider). Prefer `useAuth()` in
 * production code paths where the provider is guaranteed to exist.
 */
export function useAuthOptional(): AuthContextType | null {
    const context = useContext(AuthContext);
    return context ?? null;
}

// ===========================================
// HOC for requiring auth
// ===========================================

interface RequireAuthProps {
    children: ReactNode;
    fallback?: ReactNode;
}

export function RequireAuth({ children, fallback }: RequireAuthProps) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return fallback || null;
    }

    if (!isAuthenticated) {
        return null;
    }

    return <>{children}</>;
}

// ===========================================
// HOC for requiring admin
// ===========================================

interface RequireAdminProps {
    children: ReactNode;
    fallback?: ReactNode;
}

export function RequireAdmin({ children, fallback }: RequireAdminProps) {
    const { isAdmin, isLoading } = useAuth();

    if (isLoading) {
        return fallback || null;
    }

    if (!isAdmin) {
        return null;
    }

    return <>{children}</>;
}

export default AuthProvider;
